import { NgModule, APP_INITIALIZER, Injector, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Title } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { AppRoutingModule } from 'src/app/app-routing.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { SharedServicesModule } from 'src/app/shared/shared-services.module';
import { TokenInterceptor } from 'src/app/auth/token.interceptor';
import { WINDOW_PROVIDERS } from 'src/app/shared/utils/window.provider';

import { AppLoadService } from 'src/app/shared/services/app-load.service';
import { AppInitializationService } from 'src/app/shared/services/app-initialization.service';
import { AuthGuardService } from 'src/app/guards/auth-guard.service';
import { AccountCreationGuardService } from 'src/app/guards/account-creation-guard.service';
import { AccountSecureLinkGuardService } from 'src/app/guards/account-secure-link-guard.service';
import { EveryoneButManagerGuard } from './guards/everyone-but-manager-guard.service';
import { ResponsiveService } from 'src/app/shared/services/responsive.service';
import { LineupErrorStateMatcher } from 'src/app/shared/services/error-state-matcher.service';
import { DatadogRumService } from 'src/app/shared/services/datadog.service';
import { EulaGuardService } from 'src/app/guards/eula-guard.service';

import { AppComponent } from 'src/app/app.component';
import { HomeComponent } from 'src/app/home/home.component';
import { PageNotFoundComponent } from 'src/app/shared/components/page-not-found/page-not-found.component';
import { CompatibilityPopupComponent } from 'src/app/shared/components/compatibility-popup/compatibility-popup.component';
import { UnsuccessfulLoginComponent } from 'src/app/shared/components/unsuccessful-login/unsuccessful-login.component';
import { ErrorTestComponent } from 'src/app/shared/components/error-test/error-test.component';
import { PasswordChangedComponent } from 'src/app/password-changed/password-changed.component';
import { EveryoneButEditorGuard } from './guards/everyone-but-editor-guard.service';
import { ToolbarComponent } from 'src/app/toolbar/toolbar.component';
import { WorkspaceNavComponent } from 'src/app/workspace-nav/workspace-nav.component';
import { LoginComponent } from './login/login.component';
import { LogoutComponent } from './logout/logout.component';
import { CreateAccountComponent } from './create-account/create-account.component';
import { CallbackComponent } from './callback/callback.component';
import { ExportsComponent } from './exports/exports.component';
import { WidgetsModule } from './widgets/widgets.module';
import { EulaComponent } from './eula/eula.component';
import { MaintenanceModeComponent } from './maintenance-mode/maintenance-mode.component';
import { AuthService } from './auth/auth.service';
import { AuthModule } from '@auth0/auth0-angular';
import { SessionExpiredComponent } from './session-expired/session-expired.component';

@NgModule({
  declarations: [
    AppComponent,
    CallbackComponent,
    ToolbarComponent,
    WorkspaceNavComponent,
    HomeComponent,
    LoginComponent,
    LogoutComponent,
    PageNotFoundComponent,
    UnsuccessfulLoginComponent,
    ErrorTestComponent,
    CompatibilityPopupComponent,
    PasswordChangedComponent,
    CreateAccountComponent,
    ExportsComponent,
    EulaComponent,
    MaintenanceModeComponent,
    SessionExpiredComponent,
  ],
  imports: [
    BrowserAnimationsModule,
    AppRoutingModule,
    WidgetsModule,
    SharedModule.forRoot(),
    SharedServicesModule.forRoot(),
    AuthModule.forRoot(),
  ],
  providers: [
    Title,
    AuthService,
    AuthGuardService,
    AccountCreationGuardService,
    AccountSecureLinkGuardService,
    EveryoneButManagerGuard,
    EveryoneButEditorGuard,
    ResponsiveService,
    LineupErrorStateMatcher,
    AppLoadService,
    DatadogRumService,
    EulaGuardService,
    {
      provide: APP_INITIALIZER,
      useFactory:
        (
          appInit: AppInitializationService,
          dataDogRum: DatadogRumService,
          authService: AuthService
        ) =>
        () =>
          appInit.load().then((appConfig) => {
            if (appConfig?.initialized) {
              authService.init();
              dataDogRum.initLineupApp(appConfig);
            }
          }),
      multi: true,
      deps: [AppInitializationService, DatadogRumService, AuthService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    WINDOW_PROVIDERS,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
